import { MenuItem, Stack } from "@mui/material";
import { Text, Checkmark2Icon, FaroTextVariant, neutral, blue } from "@flat-ui";
import { EDecimalToHex } from "@styles/common-colors";
import { addTransparency } from "@utils/ui-utils";
import { ValueLabelItem } from "@custom-types/types";
import { SphereDropdownSelectorProps } from "@components/common/dropdown/sphere-dropdown-selector-types";

export type SphereDropdownMenuItemProps<T extends string | number> = Pick<
  SphereDropdownSelectorProps<T>,
  "handleChange" | "currentValue" | "sxItem" | "sxDescription"
> & {
  item: ValueLabelItem<T>;
};

/**
 * Renders a single selectable item inside the `SphereDropdownSelector` menu.
 *
 * @template T - The value type of the dropdown, constrained to `string | number`.
 */
export function SphereDropdownMenuItem<T extends string | number>({
  item,
  currentValue,
  handleChange,
  sxItem,
  sxDescription,
}: SphereDropdownMenuItemProps<T>): JSX.Element {
  const isSelected = item.value === currentValue;

  return (
    <MenuItem
      key={item.value}
      value={item.value}
      data-testid={`dropdown-item-${item.value}`}
      aria-label={`Select ${item.value}`}
      selected={isSelected}
      onClick={() => handleChange(item.value)}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        py: 0.5,
        px: 1,
        "&:hover": {
          backgroundColor: addTransparency({
            color: neutral[500],
            alpha: EDecimalToHex.twentySix,
          }),
        },
        ...sxItem,
      }}
    >
      <Stack direction="row" alignItems="center" gap="0.75rem" width="100%">
        <Text
          variant={FaroTextVariant.bodyM}
          color={isSelected ? blue[500] : "inherit"}
        >
          {item.label}
        </Text>

        {item.icon}

        {isSelected && (
          <Checkmark2Icon
            data-testid="checkmark-icon"
            sx={{
              width: "1.25rem",
              height: "1.25rem",
              fill: blue[500],
            }}
          />
        )}
      </Stack>

      {item.description && (
        <Text
          variant={FaroTextVariant.bodyS}
          sx={{
            color: neutral[600],
            mt: 0.25,
            ...sxDescription,
          }}
        >
          {item.description}
        </Text>
      )}
    </MenuItem>
  );
}
